import React, {useEffect, useMemo, useState} from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  approveRegistrationRequest,
  fetchRegistrationRequests,
  rejectRegistrationRequest,
  RegistrationRequest,
  RegistrationRequestStatus,
} from "../../api/auth";
import {createTenant, fetchTenants, Tenant, TenantStatus} from "../../api/tenant";
import {UserRole, roleLabels} from "../../api/user";
import ThemeSelector from "../../components/common/ThemeSelector";

const statusColorMap: Record<RegistrationRequestStatus, "warning" | "success" | "error"> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

const AccessRequestsPage: React.FC = () => {
  const [requests, setRequests] = useState<RegistrationRequest[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<"all" | RegistrationRequestStatus>("pending");
  const [selectedRequest, setSelectedRequest] = useState<RegistrationRequest | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [createTenantDialogOpen, setCreateTenantDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: "success" | "error"}>({
    open: false,
    message: "",
    severity: "success",
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [tenantCreating, setTenantCreating] = useState(false);

  const [approveData, setApproveData] = useState({
    tenantId: "",
    role: UserRole.EMPLOYEE,
    password: "Password@123",
    reviewNotes: "",
  });
  const [rejectNotes, setRejectNotes] = useState("");
  const [newTenantData, setNewTenantData] = useState({
    name: "",
    theme: "default",
    status: TenantStatus.ACTIVE,
    description: "",
    contactEmail: "",
    contactPhone: "",
    address: "",
  });

  const roleOptions = useMemo(() => [UserRole.TENANT_ADMIN, UserRole.FARMER_ADMIN, UserRole.SUPERVISOR, UserRole.EMPLOYEE], []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [requestData, tenantData] = await Promise.all([
        fetchRegistrationRequests(statusFilter === "all" ? undefined : statusFilter),
        fetchTenants(),
      ]);
      setRequests(requestData);
      setTenants(tenantData);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to load access requests",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const handleOpenApprove = (request: RegistrationRequest) => {
    setSelectedRequest(request);
    setApproveData({
      tenantId: "",
      role: UserRole.EMPLOYEE,
      password: "Password@123",
      reviewNotes: "",
    });
    setApproveDialogOpen(true);
  };

  const handleOpenCreateTenant = () => {
    setNewTenantData({
      name: selectedRequest?.farmName || "",
      theme: "default",
      status: TenantStatus.ACTIVE,
      description: "",
      contactEmail: selectedRequest?.email || "",
      contactPhone: selectedRequest?.phone || "",
      address: "",
    });
    setCreateTenantDialogOpen(true);
  };

  const handleCreateTenant = async () => {
    if (!newTenantData.name.trim()) {
      setSnackbar({
        open: true,
        message: "Tenant name is required",
        severity: "error",
      });
      return;
    }

    try {
      setTenantCreating(true);
      const createdTenant = await createTenant({
        name: newTenantData.name.trim(),
        theme: newTenantData.theme,
        status: newTenantData.status,
        description: newTenantData.description.trim() || undefined,
        contactEmail: newTenantData.contactEmail.trim() || undefined,
        contactPhone: newTenantData.contactPhone.trim() || undefined,
        address: newTenantData.address.trim() || undefined,
      });

      const refreshedTenants = await fetchTenants();
      setTenants(refreshedTenants);
      setApproveData((prev) => ({...prev, tenantId: createdTenant.id}));
      setCreateTenantDialogOpen(false);
      setSnackbar({
        open: true,
        message: `Tenant "${createdTenant.name}" created`,
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to create tenant",
        severity: "error",
      });
    } finally {
      setTenantCreating(false);
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest || !selectedRequest.id?.trim()) {
      setSnackbar({
        open: true,
        message: "Invalid request selected. Please refresh and try again.",
        severity: "error",
      });
      return;
    }

    if (!approveData.tenantId || !approveData.password) {
      setSnackbar({
        open: true,
        message: "Tenant and password are required",
        severity: "error",
      });
      return;
    }

    try {
      setActionLoading(true);
      await approveRegistrationRequest(selectedRequest.id.trim(), {
        tenantId: approveData.tenantId,
        role: approveData.role,
        password: approveData.password,
        reviewNotes: approveData.reviewNotes || undefined,
      });
      setSnackbar({open: true, message: "Request approved and set-password email sent", severity: "success"});
      setApproveDialogOpen(false);
      setSelectedRequest(null);
      await loadData();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to approve request",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenReject = (request: RegistrationRequest) => {
    setSelectedRequest(request);
    setRejectNotes("");
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!selectedRequest || !selectedRequest.id?.trim()) {
      setSnackbar({
        open: true,
        message: "Invalid request selected. Please refresh and try again.",
        severity: "error",
      });
      return;
    }
    try {
      setActionLoading(true);
      await rejectRegistrationRequest(selectedRequest.id.trim(), rejectNotes || undefined);
      setSnackbar({open: true, message: "Request rejected", severity: "success"});
      setRejectDialogOpen(false);
      setSelectedRequest(null);
      await loadData();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Failed to reject request",
        severity: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Access Requests</Typography>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as "all" | RegistrationRequestStatus)}
          sx={{minWidth: 180}}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
        </TextField>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Farm / Org</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requested On</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.name}</TableCell>
                    <TableCell>{request.email}</TableCell>
                    <TableCell>{request.phone}</TableCell>
                    <TableCell>{request.farmName}</TableCell>
                    <TableCell>
                      <Chip label={request.status.toUpperCase()} color={statusColorMap[request.status]} size="small" />
                    </TableCell>
                    <TableCell>{new Date(request.createdAt).toLocaleString()}</TableCell>
                    <TableCell>{request.reviewNotes || request.message || "-"}</TableCell>
                    <TableCell align="right">
                      {request.status === "pending" ? (
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Button size="small" variant="contained" onClick={() => handleOpenApprove(request)} disabled={!request.id?.trim()}>
                            Approve
                          </Button>
                          <Button size="small" color="error" variant="outlined" onClick={() => handleOpenReject(request)} disabled={!request.id?.trim()}>
                            Reject
                          </Button>
                        </Box>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              {!loading && requests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No access requests found
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading access requests...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Access Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box mt={1} mb={2} p={2} border="1px solid" borderColor="divider" borderRadius={1} bgcolor="background.default">
              <Typography variant="subtitle2" gutterBottom>
                <b style={{color: "blue"}}>Request Details:</b>
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {selectedRequest.name}
              </Typography>
              <Typography variant="body2">
                <strong>Email:</strong> {selectedRequest.email}
              </Typography>
              <Typography variant="body2">
                <strong>Phone:</strong> {selectedRequest.phone}
              </Typography>
              <Typography variant="body2">
                <strong>Farm / Organization:</strong> {selectedRequest.farmName}
              </Typography>
              <Typography variant="body2">
                <strong>Submitted:</strong> {new Date(selectedRequest.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{mt: 1}}>
                <strong>Applicant Message:</strong> {selectedRequest.message || "-"}
              </Typography>
            </Box>
          )}
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button size="small" variant="outlined" onClick={handleOpenCreateTenant} disabled={actionLoading}>
              Add Tenant
            </Button>
          </Box>
          <TextField
            select
            fullWidth
            label="Tenant"
            value={approveData.tenantId}
            onChange={(e) => setApproveData((prev) => ({...prev, tenantId: e.target.value}))}
            margin="normal"
            required>
            {tenants.map((tenant) => (
              <MenuItem key={tenant.id} value={tenant.id}>
                {tenant.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="Role"
            value={approveData.role}
            onChange={(e) => setApproveData((prev) => ({...prev, role: e.target.value as UserRole}))}
            margin="normal">
            {roleOptions.map((role) => (
              <MenuItem key={role} value={role}>
                {roleLabels[role]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            label="Initial Password"
            type="text"
            value={approveData.password}
            onChange={(e) => setApproveData((prev) => ({...prev, password: e.target.value}))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Review Notes (optional)"
            value={approveData.reviewNotes}
            onChange={(e) => setApproveData((prev) => ({...prev, reviewNotes: e.target.value}))}
            margin="normal"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleApprove} variant="contained" disabled={actionLoading}>
            Approve & Create User
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createTenantDialogOpen} onClose={() => setCreateTenantDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tenant</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Tenant Name"
            value={newTenantData.name}
            onChange={(e) => setNewTenantData((prev) => ({...prev, name: e.target.value}))}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={newTenantData.description}
            onChange={(e) => setNewTenantData((prev) => ({...prev, description: e.target.value}))}
            margin="normal"
            multiline
            minRows={2}
          />
          <Box mt={2}>
            <ThemeSelector
              value={newTenantData.theme}
              onChange={(theme) => setNewTenantData((prev) => ({...prev, theme}))}
              showPreview={false}
              label="Theme"
              size="small"
            />
          </Box>
          <TextField
            select
            fullWidth
            label="Status"
            value={newTenantData.status}
            onChange={(e) => setNewTenantData((prev) => ({...prev, status: e.target.value as TenantStatus}))}
            margin="normal">
            <MenuItem value={TenantStatus.ACTIVE}>Active</MenuItem>
            <MenuItem value={TenantStatus.INACTIVE}>Inactive</MenuItem>
            <MenuItem value={TenantStatus.SUSPENDED}>Suspended</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Contact Email (optional)"
            value={newTenantData.contactEmail}
            onChange={(e) => setNewTenantData((prev) => ({...prev, contactEmail: e.target.value}))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Contact Phone (optional)"
            value={newTenantData.contactPhone}
            onChange={(e) => setNewTenantData((prev) => ({...prev, contactPhone: e.target.value}))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Address (optional)"
            value={newTenantData.address}
            onChange={(e) => setNewTenantData((prev) => ({...prev, address: e.target.value}))}
            margin="normal"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTenantDialogOpen(false)} disabled={tenantCreating}>
            Cancel
          </Button>
          <Button onClick={handleCreateTenant} variant="contained" disabled={tenantCreating}>
            Create Tenant
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Access Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason (optional)"
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
            margin="normal"
            multiline
            minRows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={actionLoading}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((prev) => ({...prev, open: false}))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({...prev, open: false}))} sx={{width: "100%"}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccessRequestsPage;
